import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/** The "How to use" walkthrough, mirroring the Cash Flow / Commissions /
 *  Planning how-to-tab pattern. Grows as the feature does. */
export function HowToTab() {
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>What this is</CardTitle>
          <CardDescription>
            A semantic index of the <span className="font-medium">main branch</span> of a
            curated set of Cinch <em>product</em> repositories — chopped into function-
            and class-sized chunks and stored as vectors, so you can find code by what it{" "}
            <em>does</em> rather than by what it's called.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            It is the same index behind the <code className="rounded bg-muted px-1">/rag</code>{" "}
            command in Claude Code. This page exists so you don't need a terminal, a VPN
            script, or a Python install to use it — and so the index's{" "}
            <span className="font-medium">freshness is visible</span> rather than assumed.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">It is a curated list, not
            everything.</span>{" "}
            The Cinch workspace holds close to 200 repositories; a deliberate subset is
            indexed. CinchOps itself is excluded on purpose, as are pricing-cinch-stack
            and cinch-admin-apps — this covers the product, not the internal tooling. The
            Overview tab is the authoritative list of what is actually in there; if a
            repository you expected is missing, it has not been onboarded rather than
            being empty.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How to use it</CardTitle>
          <CardDescription>
            The working tabs, in the order you'd normally reach for them.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal space-y-3 pl-5 text-sm">
            <li>
              <span className="font-medium">Overview</span> — start here, and check the age
              of the repo you care about before trusting anything else on the page. Clicking
              a repository name jumps straight into a search scoped to it.
            </li>
            <li>
              <span className="font-medium">Search</span> — describe the behaviour you're
              looking for in a sentence. "Where subscription MRR is calculated for a contract"
              works; <code className="rounded bg-muted px-1">calculateMRR</code> works badly.
              Narrow with the repository and language filters when a query pulls in the wrong
              neighbourhood. Each result expands to show the code, and carries the author,
              date, and commit message of the last change.
            </li>
            <li>
              <span className="font-medium">Ask</span> — for questions rather than lookups
              ("how does X talk to Y?"). It retrieves chunks first, then answers only from
              them, and lists every source it used underneath. Read the sources: they are the
              point. An answer whose citations don't support it is an answer to discard. You
              can pick a different model, and the repository and language filters apply here
              too.
            </li>
            <li>
              <span className="font-medium">Manage Qdrant</span> — admin-only. Collection
              configuration, the health of the payload indexes, and a link to Qdrant's own
              console.
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Reading the freshness board</CardTitle>
          <CardDescription>
            The single most important thing on this page, and the easiest to misread.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="flex flex-col gap-3 text-sm">
            <div>
              <dt className="font-medium">Indexed through</dt>
              <dd className="text-muted-foreground">
                The date of the newest commit that is actually <em>present in the index</em>{" "}
                for that repository — measured from the indexed code itself, not from a job
                log. If it says 14 August, then nothing committed after 14 August exists here,
                full stop.
              </dd>
            </div>
            <div>
              <dt className="font-medium">Last sync run</dt>
              <dd className="text-muted-foreground">
                When the nightly job last finished. A <em>separate</em> signal on purpose: the
                job can run successfully and still index nothing new, and it can die silently
                while the index quietly ages. Watching only one of these is how the index sat
                five weeks stale without anyone noticing.
              </dd>
            </div>
            <div>
              <dt className="font-medium">Which branch you are searching</dt>
              <dd className="text-muted-foreground">
                <span className="font-medium text-foreground">main</span>, for every
                repository — the backend and frontend included. So what you find here
                lines up with what has actually been released, not with whatever is
                mid-flight on a feature or dev branch. Two consequences worth holding
                onto: work that has been merged but not yet deployed <em>is</em> in
                here, and work still open in a branch is <em>not</em>, no matter how
                far along it is.
              </dd>
            </div>
            <div>
              <dt className="font-medium">A repo that looks very old</dt>
              <dd className="text-muted-foreground">
                Usually means the repository itself is dormant, not that the sync is broken —
                <code className="rounded bg-muted px-1">cinch_mcp_snowflake</code> genuinely
                hasn't changed since March. The banner at the top of the Overview tab is what
                tells you the pipeline is broken, and it appears once the sync hasn't completed
                in 48 hours.
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>When to use something else</CardTitle>
          <CardDescription>
            This is a good tool with a narrow edge. Knowing where the edge is saves time.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            <li>
              <span className="font-medium text-foreground">Looking for an exact symbol,
              error string, or config key?</span>{" "}
              Grep the repository. Semantic search is built for the fuzzy question, and it will
              cheerfully return five near-misses instead of the one exact hit.
            </li>
            <li>
              <span className="font-medium text-foreground">Need to know what's live right
              now?</span>{" "}
              Check the deploy, not this. The index is a point-in-time snapshot of the default
              branch.
            </li>
            <li>
              <span className="font-medium text-foreground">Results from{" "}
              <code className="rounded bg-muted px-1">frontend-st</code> look vague?</span>{" "}
              That's the chunking. Files the parser can't split cleanly fall back to a sliding
              window, and those chunks can span roughly a thousand lines — so the line range is
              a neighbourhood, not a pinpoint. Open the file to confirm.
            </li>
            <li>
              <span className="font-medium text-foreground">Nothing loads at all?</span>{" "}
              The index lives on the Spark host. Off the VPN, the page will tell you it's
              unreachable rather than showing you an empty result set.
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Access</CardTitle>
          <CardDescription>Three permissions, granted deliberately.</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="flex flex-col gap-3 text-sm">
            <div>
              <dt className="font-medium">
                <code className="rounded bg-muted px-1">code_rag.read</code>
              </dt>
              <dd className="text-muted-foreground">
                Search, ask, and view freshness. Worth granting consciously: results include
                the actual source, so this is effectively read access to every indexed product
                repository. It is deliberately excluded from blanket "Monitors" role grants for
                that reason.
              </dd>
            </div>
            <div>
              <dt className="font-medium">
                <code className="rounded bg-muted px-1">code_rag.admin</code>
              </dt>
              <dd className="text-muted-foreground">
                Adds the Manage Qdrant tab. Sits outside the usual read/write pair, so no
                category-level role grant can ever hand it out by accident — it has to be
                given directly.
              </dd>
            </div>
            <div>
              <dt className="font-medium">
                <code className="rounded bg-muted px-1">code_rag.write</code>
              </dt>
              <dd className="text-muted-foreground">
                Reserved for saved searches and pinned answers. Nothing uses it yet.
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}
